import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TrainerLayout from '../../components/layout/TrainerLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ClipboardList, Plus, Loader2, ChevronRight, Globe, Copy } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import ClonePlanDialog from '../../components/admin/ClonePlanDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrainerPlansPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cloneTarget, setCloneTarget] = useState(null);

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/trainer/plans`);
      setData(res.data);
    } catch (e) {
      toast.error('Error al cargar planes');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <TrainerLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </TrainerLayout>
    );
  }

  const myPlans = data?.my_plans || [];
  const globalPlans = data?.global_plans || [];

  return (
    <TrainerLayout>
      <div className="space-y-8" data-testid="trainer-plans">
        {/* My Plans */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Mis Planes</h1>
              <p className="text-muted-foreground">{myPlans.length} planes propios</p>
            </div>
            <Button onClick={() => navigate('/trainer/plans/new')} data-testid="create-plan-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Plan
            </Button>
          </div>

          {myPlans.length === 0 ? (
            <Card className="bg-card border-border border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">A{'\u00fa'}n no has creado planes</p>
                <Button onClick={() => navigate('/trainer/plans/new')}>
                  <Plus className="h-4 w-4 mr-2" /> Crear primer plan
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myPlans.map((plan) => (
                <Card key={plan.id} className="bg-card border-border card-hover relative">
                  <CardContent className="p-5" onClick={() => navigate(`/trainer/plans/${plan.id}`)} style={{ cursor: 'pointer' }}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-foreground">{plan.name}</h3>
                        <p className="text-xs text-muted-foreground mt-1">{plan.objective || ''}</p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge variant="outline" className="text-xs">{plan.weeks?.length || 0} semanas</Badge>
                    </div>
                  </CardContent>
                  <div className="px-5 pb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={(e) => { e.stopPropagation(); setCloneTarget(plan); }}
                      data-testid={`clone-plan-${plan.id}`}
                    >
                      <Copy className="h-3.5 w-3.5 mr-1.5" />
                      Clonar a otro cliente
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Info sobre planes globales */}
        <Card className="bg-card border-border border-dashed">
          <CardContent className="p-5 text-center">
            <p className="text-sm text-muted-foreground">
              Si necesitas acceso a planes de GuardiansFit, sol{'\u00ed'}citalo al administrador.
            </p>
          </CardContent>
        </Card>
      </div>

      <ClonePlanDialog
        open={!!cloneTarget}
        onClose={() => setCloneTarget(null)}
        plan={cloneTarget}
        role="trainer"
        onCloned={() => fetchPlans()}
      />
    </TrainerLayout>
  );
};

export default TrainerPlansPage;
